import { useEffect } from 'react'
import { currentAdminCan } from '../data/currentAdminAccess'

const PROJECT_STAGES = ['견적 선택', '결제 완료', '상담·시안', '최종 확정', '진행 완료']
const SETTLEMENT_STATE_PREFIX = 'difan-admin-settlement-workflow-v2:'
const CARRYOVER_KEY = 'difan-admin-settlement-carryovers-v1'
const BATCH_KEY = 'difan-admin-settlement-batches-v1'

const text = (value) => String(value || '').replace(/\s+/g, ' ').trim()
const positiveMoney = (value) => Math.abs(Number(String(value || '').replace(/[^0-9-]/g, '')) || 0)
const signedMoney = (value) => Number(String(value || '').replace(/[^0-9-]/g, '')) || 0
const won = (value) => `${Number(value || 0).toLocaleString('ko-KR')}원`
const read = (key, fallback) => { try { return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback } catch { return fallback } }
const write = (key, value) => { try { localStorage.setItem(key, JSON.stringify(value)) } catch {} }
const pageTitle = () => document.querySelector('.admin-content .page-title-row h1')
const pageDescription = () => document.querySelector('.admin-content .page-title-row p')
const findCard = (name) => Array.from(document.querySelectorAll('.admin-content .card')).find((node) => text(node.querySelector('h2')?.textContent).includes(name))

function normalizeProjectStage(raw, status = '') {
  const value = text(raw)
  const normalizedStatus = text(status).toUpperCase()
  if (/완료/.test(value) || normalizedStatus === 'COMPLETED') return '진행 완료'
  if (/견적/.test(value)) return '견적 선택'
  if (/결제/.test(value) || normalizedStatus === 'READY') return '결제 완료'
  if (/최종 확정|제작|설치|행사/.test(value)) return '최종 확정'
  if (/상담|시안|디자인|소재|컨셉|초기/.test(value)) return '상담·시안'
  return '상담·시안'
}

function removeTableColumn(table, label) {
  const headers = Array.from(table?.querySelectorAll('thead th') || [])
  const index = headers.findIndex((th) => text(th.textContent) === label)
  if (index < 0) return
  headers[index].remove()
  table.querySelectorAll('tbody tr').forEach((row) => row.children[index]?.remove())
}

function applyDirectorApprovalPolicy(route) {
  document.querySelectorAll('.admin-sidebar .nav-submenu button').forEach((node) => {
    if (text(node.textContent) === '심사 관리') node.textContent = '승인 관리'
  })

  if (route === '/dashboard') {
    document.querySelectorAll('.admin-content strong,.admin-content h2').forEach((node) => {
      if (text(node.textContent) === '디렉터 심사 대기') node.textContent = '디렉터 승인 대기'
    })
    document.querySelectorAll('.admin-content p').forEach((node) => {
      if (text(node.textContent).includes('심사 요청')) node.textContent = text(node.textContent).replace('심사 요청', '승인 요청')
    })
  }

  if (route === '/director-reviews') {
    document.title = '디렉터 승인 | DIFAN ADMIN'
    if (pageTitle()) pageTitle().textContent = '디렉터 승인 관리'
    if (pageDescription()) pageDescription().textContent = '사업자 정보와 계정 기본 자격을 확인해 활동 승인 여부를 관리합니다. 포트폴리오는 승인 심사 대상이 아닙니다.'
    document.querySelectorAll('.filter-buttons button').forEach((node) => {
      if (text(node.textContent) === '심사 필요') node.textContent = '승인 검토'
    })
    const table = document.querySelector('.admin-content table.data-table')
    removeTableColumn(table, '포트폴리오')
    table?.querySelectorAll('th').forEach((node) => {
      if (text(node.textContent) === '심사 상태') node.textContent = '승인 상태'
    })
  }

  if (!/^\/directors\/[^/]+$/.test(route)) return

  document.querySelectorAll('.tabs button').forEach((node) => {
    if (text(node.textContent) === '심사 요약') node.textContent = '승인 요약'
  })
  document.querySelectorAll('.detail-summary-bar span').forEach((node) => {
    if (text(node.textContent) === '심사 상태') node.textContent = '승인 상태'
    if (text(node.textContent) === '심사 요청일') node.textContent = '승인 요청일'
  })

  const reviewCard = findCard('심사 체크포인트') || findCard('승인 확인 항목')
  if (reviewCard) {
    const heading = reviewCard.querySelector('h2')
    if (heading) heading.textContent = '승인 확인 항목'
    let description = reviewCard.querySelector('.card-header p')
    if (!description) {
      description = document.createElement('p')
      reviewCard.querySelector('.card-header > div')?.appendChild(description)
    }
    if (description) description.textContent = '사업자·계정·필수 운영 정보만 확인합니다. 포트폴리오의 작품성·완성도는 승인 조건이 아니며 사전심사하지 않습니다.'
    reviewCard.querySelectorAll('.review-check-list > div').forEach((row) => {
      if (text(row.textContent).includes('포트폴리오')) row.remove()
    })
  }

  const portfolioCard = findCard('공개 포트폴리오')
  if (portfolioCard) {
    let description = portfolioCard.querySelector('.card-header p')
    if (!description) {
      description = document.createElement('p')
      portfolioCard.querySelector('.card-header > div')?.appendChild(description)
    }
    if (description) description.textContent = '디렉터가 직접 등록한 공개 콘텐츠를 운영 참고용으로 조회합니다. 디렉터 승인과 무관하며 DIFAN 관리자가 사전심사하지 않습니다.'
  }
}

function summaryItem(label) {
  return Array.from(document.querySelectorAll('.admin-content .summary-grid > *')).find((node) => text(node.querySelector('span')?.textContent) === label)
}

function summaryValue(node) {
  return text(node?.querySelector('strong')?.textContent || node?.lastElementChild?.textContent)
}

function setSummary(node, value) {
  const target = node?.querySelector('strong') || node?.lastElementChild
  if (target) target.textContent = value
}

function applyProjectParity(route) {
  if (route === '/projects') {
    if (pageDescription()) pageDescription().textContent = '사용자 화면과 동일한 큰 진행 단계만 확인합니다. 제작·설치 등 세부 작업은 활동 이력으로 관리하며 날짜만으로 자동 전환하지 않습니다.'
    const table = document.querySelector('.admin-content table.data-table')
    const headers = Array.from(table?.querySelectorAll('thead th') || [])
    const stageIndex = headers.findIndex((th) => text(th.textContent) === '현재 단계')
    const statusIndex = headers.findIndex((th) => text(th.textContent) === '상태')
    if (stageIndex >= 0) {
      table?.querySelectorAll('tbody tr').forEach((row) => {
        const cell = row.children[stageIndex]
        if (!cell) return
        cell.dataset.rawStage ||= text(cell.textContent)
        cell.textContent = normalizeProjectStage(cell.dataset.rawStage, statusIndex >= 0 ? row.children[statusIndex]?.textContent : '')
      })
    }
    removeTableColumn(table, '진행률')
  }

  if (!/^\/projects\/[^/]+$/.test(route)) return

  if (pageDescription()) pageDescription().textContent = '사용자 화면과 같은 프로젝트 진행 단계와 거래 원장을 확인합니다. 세부 제작 업무를 별도 상태값으로 자동 전환하지 않습니다.'
  const stage = summaryItem('현재 단계')
  const status = summaryItem('프로젝트 상태')
  if (stage) {
    stage.dataset.rawStage ||= summaryValue(stage)
    setSummary(stage, normalizeProjectStage(stage.dataset.rawStage, summaryValue(status)))
  }
  summaryItem('진행률')?.remove()

  const milestoneCard = findCard('진행 마일스톤') || findCard('사용자 화면 기준 진행 단계')
  if (!milestoneCard) return
  const heading = milestoneCard.querySelector('h2')
  if (heading) heading.textContent = '사용자 화면 기준 진행 단계'
  let description = milestoneCard.querySelector('.card-header p')
  if (!description) {
    description = document.createElement('p')
    milestoneCard.querySelector('.card-header > div')?.appendChild(description)
  }
  if (description) description.textContent = '견적 선택 → 결제 완료 → 상담·시안 → 최종 확정 → 진행 완료. 실제 진행 확인 후 변경하며 예정일만으로 자동 변경하지 않습니다.'

  const track = milestoneCard.querySelector('.project-milestones')
  if (!track || !stage) return
  const currentStage = normalizeProjectStage(stage.dataset.rawStage, summaryValue(status))
  if (track.dataset.projectParity === currentStage) return
  const currentIndex = PROJECT_STAGES.indexOf(currentStage)
  track.replaceChildren(...PROJECT_STAGES.map((label, index) => {
    const state = index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'upcoming'
    const row = document.createElement('div')
    row.className = `milestone-${state}`
    const dot = document.createElement('i')
    const copy = document.createElement('div')
    const strong = document.createElement('strong')
    const small = document.createElement('span')
    const badge = document.createElement('span')
    strong.textContent = label
    small.textContent = state === 'done' ? '완료' : state === 'current' ? '현재 단계' : '예정'
    badge.className = 'status-badge'
    badge.textContent = state === 'done' ? '완료' : state === 'current' ? '현재' : '예정'
    copy.append(strong, small)
    row.append(dot, copy, badge)
    return row
  }))
  track.dataset.projectParity = currentStage
}

const settlementStateKey = (route) => `${SETTLEMENT_STATE_PREFIX}${route}`
const settlementState = (route) => read(settlementStateKey(route), {})
const saveSettlementState = (route, state) => write(settlementStateKey(route), state)
const settlementCard = () => findCard('정산 대상')
const settlementRows = () => Array.from(settlementCard()?.querySelectorAll('tbody tr[data-settlement-key]') || [])

function nextBatchName(batchNo) {
  const match = text(batchNo).match(/^(\d{4}-\d{2})-(\d+)차$/)
  return match ? `${match[1]}-${Number(match[2]) + 1}차` : '다음 정산 회차'
}

function showNotice(message) {
  const root = document.querySelector('.admin-content')
  if (!root) return
  let node = root.querySelector('[data-workflow-notice]')
  if (!node) {
    node = document.createElement('div')
    node.dataset.workflowNotice = '1'
    node.className = 'admin-notice'
    root.prepend(node)
  }
  node.replaceChildren()
  const strong = document.createElement('strong')
  const span = document.createElement('span')
  strong.textContent = '안내'
  span.textContent = message
  node.append(strong, span)
}

function rowData(row) {
  return {
    director: text(row.querySelector('[data-field="director"]')?.textContent),
    gross: positiveMoney(row.querySelector('[data-field="gross"]')?.textContent),
    refund: positiveMoney(row.querySelector('[data-field="refund"]')?.textContent),
    pgFee: positiveMoney(row.querySelector('[data-field="pgFee"]')?.textContent),
    adjustment: signedMoney(row.querySelector('[data-field="adjustment"]')?.textContent),
    payout: positiveMoney(row.querySelector('[data-field="payout"]')?.textContent),
    feeStatus: text(row.querySelector('[data-field="feeStatus"]')?.textContent),
    account: text(row.querySelector('[data-field="account"]')?.textContent),
  }
}

function appendCarryovers(table, batchNo) {
  const body = table.querySelector('tbody')
  if (!body) return
  const existingCarryovers = new Set(Array.from(body.querySelectorAll('tr[data-carryover-id]')).map((row) => row.dataset.carryoverId))
  read(CARRYOVER_KEY, []).filter((item) => item.targetBatch === batchNo && !existingCarryovers.has(item.id)).forEach((item) => {
    const row = document.createElement('tr')
    row.dataset.carryoverId = item.id
    const values = [item.director, won(item.gross), `-${won(item.refund)}`, `-${won(item.pgFee)}`, item.adjustment >= 0 ? `+${won(item.adjustment)}` : `-${won(Math.abs(item.adjustment))}`, won(item.payout), item.feeStatus || 'CONFIRMED', item.account]
    values.forEach((value, index) => {
      const cell = document.createElement('td')
      if (index === 0 || index === 5) {
        const strong = document.createElement('strong')
        strong.textContent = value
        cell.appendChild(strong)
      } else cell.textContent = value
      row.appendChild(cell)
    })
    body.appendChild(row)
  })
}

function buildSettlementTable(route) {
  const target = settlementCard()
  const table = target?.querySelector('table.data-table')
  if (!target || !table || table.dataset.workflowVersion === '2') return

  const batchNo = text(pageTitle()?.textContent)
  appendCarryovers(table, batchNo)

  const head = table.querySelector('thead tr')
  const selectHead = document.createElement('th')
  selectHead.textContent = '선택'
  head?.prepend(selectHead)
  ;['처리 상태', '관리'].forEach((label) => {
    const th = document.createElement('th')
    th.textContent = label
    head?.appendChild(th)
  })

  const fields = ['director', 'gross', 'refund', 'pgFee', 'adjustment', 'payout', 'feeStatus', 'account']
  table.querySelectorAll('tbody tr').forEach((row, index) => {
    if (row.querySelector('.admin-table-empty')) return
    Array.from(row.children).forEach((cell, cellIndex) => { if (fields[cellIndex]) cell.dataset.field = fields[cellIndex] })
    const key = row.dataset.carryoverId || `${text(row.querySelector('[data-field="director"]')?.textContent)}:${index}`
    row.dataset.settlementKey = key
    const selectCell = document.createElement('td')
    const checkbox = document.createElement('input')
    checkbox.type = 'checkbox'
    checkbox.dataset.settlementSelect = '1'
    selectCell.appendChild(checkbox)
    row.prepend(selectCell)
    const statusCell = document.createElement('td')
    statusCell.dataset.settlementStatus = '1'
    const actionCell = document.createElement('td')
    actionCell.dataset.settlementActions = '1'
    row.append(statusCell, actionCell)
  })

  const toolbar = document.createElement('div')
  toolbar.className = 'table-toolbar'
  toolbar.dataset.settlementToolbar = '1'
  const selectLabel = document.createElement('label')
  selectLabel.className = 'filter-buttons'
  const selectAll = document.createElement('input')
  selectAll.type = 'checkbox'
  selectAll.dataset.selectAll = '1'
  selectLabel.append(selectAll, document.createTextNode(' 전체 선택'))
  const actions = document.createElement('div')
  actions.className = 'filter-buttons'
  ;[['approve-selected', '선택 승인'], ['defer-selected', '선택 다음 회차 이월'], ['approve-all', '검토중 전체 승인']].forEach(([key, label]) => {
    const button = document.createElement('button')
    button.type = 'button'
    button.dataset.bulk = key
    button.textContent = label
    actions.appendChild(button)
  })
  toolbar.append(selectLabel, actions)
  target.querySelector('.table-scroll')?.before(toolbar)
  table.dataset.workflowVersion = '2'
}

function settlementTotals(route) {
  const state = settlementState(route)
  return settlementRows().reduce((result, row) => {
    const item = state[row.dataset.settlementKey] || { status: 'PENDING' }
    const payout = rowData(row).payout
    result[item.status] = (result[item.status] || 0) + 1
    if (item.status === 'APPROVED') result.approvedAmount += payout
    if (item.status === 'DEFERRED') result.deferredAmount += payout
    return result
  }, { PENDING: 0, APPROVED: 0, DEFERRED: 0, approvedAmount: 0, deferredAmount: 0 })
}

function createRowButton(label, action, key, allowed) {
  const button = document.createElement('button')
  button.type = 'button'
  button.className = 'button soft'
  button.dataset.rowAction = action
  button.dataset.key = key
  button.textContent = label
  button.disabled = !allowed
  if (!allowed) button.title = '이 계정에는 승인·처리 권한이 없습니다.'
  return button
}

function refreshSettlement(route) {
  buildSettlementTable(route)
  const state = settlementState(route)
  const allowed = currentAdminCan(route, 'approve')

  settlementRows().forEach((row) => {
    const key = row.dataset.settlementKey
    const item = state[key] || { status: 'PENDING' }
    const statusCell = row.querySelector('[data-settlement-status]')
    const actionCell = row.querySelector('[data-settlement-actions]')
    const checkbox = row.querySelector('[data-settlement-select]')
    if (!statusCell || !actionCell || !checkbox) return

    statusCell.replaceChildren()
    const badge = document.createElement('span')
    badge.className = 'status-badge'
    badge.textContent = item.status === 'APPROVED' ? '승인' : item.status === 'DEFERRED' ? '다음 회차 이월' : '검토중'
    statusCell.appendChild(badge)
    if (item.status === 'DEFERRED' && item.reason) {
      const small = document.createElement('small')
      small.textContent = item.reason
      statusCell.appendChild(small)
    }

    checkbox.disabled = item.status !== 'PENDING' || !allowed
    actionCell.replaceChildren()
    if (item.status === 'PENDING') {
      actionCell.append(createRowButton('승인', 'approve', key, allowed), document.createTextNode(' '), createRowButton('다음 회차 이월', 'defer', key, allowed))
    } else {
      const small = document.createElement('small')
      small.textContent = item.status === 'DEFERRED' ? item.targetBatch || '다음 회차' : '처리 완료'
      actionCell.appendChild(small)
    }
  })

  settlementCard()?.querySelectorAll('[data-settlement-toolbar] button').forEach((button) => {
    button.disabled = !allowed
    if (!allowed) button.title = '이 계정에는 승인·처리 권한이 없습니다.'
  })

  const totals = settlementTotals(route)
  const toolbar = settlementCard()?.querySelector('[data-settlement-toolbar]')
  let summary = settlementCard()?.querySelector('[data-settlement-summary]')
  if (!summary && toolbar) {
    summary = document.createElement('div')
    summary.dataset.settlementSummary = '1'
    summary.className = 'table-result-bar'
    toolbar.after(summary)
  }
  if (summary) {
    summary.replaceChildren()
    const span = document.createElement('span')
    span.innerHTML = `검토중 <strong>${totals.PENDING}</strong> · 승인 ${totals.APPROVED}건 ${won(totals.approvedAmount)} · 이월 ${totals.DEFERRED}건 ${won(totals.deferredAmount)}`
    const small = document.createElement('small')
    small.textContent = '개별/선택/전체 처리 가능'
    summary.append(span, small)
  }

  const payoutSummary = summaryItem('최종 지급액') || summaryItem('이번 회차 승인 지급액')
  if (payoutSummary) {
    const label = payoutSummary.querySelector('span')
    if (label) label.textContent = '이번 회차 승인 지급액'
    setSummary(payoutSummary, won(totals.approvedAmount))
  }

  document.querySelectorAll('.admin-content button').forEach((button) => {
    if (text(button.textContent) !== '회차 확정') return
    const blocked = totals.PENDING > 0 || !allowed
    button.dataset.workflowBlocked = blocked ? 'true' : 'false'
    button.disabled = blocked
    button.style.opacity = blocked ? '.5' : ''
    button.style.cursor = blocked ? 'not-allowed' : ''
    if (totals.PENDING > 0) button.title = `검토중 ${totals.PENDING}건을 먼저 승인 또는 이월해주세요.`
    else if (!allowed) button.title = '이 계정에는 승인·처리 권한이 없습니다.'
    else button.removeAttribute('title')
  })
}

function selectedSettlementKeys() {
  return settlementRows().filter((row) => row.querySelector('[data-settlement-select]')?.checked).map((row) => row.dataset.settlementKey)
}

function approveSettlement(route, keys) {
  if (!currentAdminCan(route, 'approve')) return showNotice('이 계정에는 승인·처리 권한이 없습니다.')
  if (!keys.length) return showNotice('승인할 정산 건을 선택해주세요.')
  const state = settlementState(route)
  let count = 0
  keys.forEach((key) => {
    if ((state[key]?.status || 'PENDING') !== 'PENDING') return
    state[key] = { status: 'APPROVED', at: new Date().toISOString() }
    count += 1
  })
  saveSettlementState(route, state)
  showNotice(`${count}건을 승인했습니다.`)
  refreshSettlement(route)
}

function openCarryoverModal(route, keys) {
  if (!currentAdminCan(route, 'approve')) return showNotice('이 계정에는 승인·처리 권한이 없습니다.')
  if (!keys.length) return showNotice('다음 회차로 이월할 정산 건을 선택해주세요.')
  if (document.querySelector('[data-carryover-modal]')) return

  const layer = document.createElement('div')
  layer.className = 'admin-modal-layer'
  layer.dataset.carryoverModal = '1'
  layer.setAttribute('role', 'presentation')
  const form = document.createElement('form')
  form.className = 'admin-modal admin-modal-medium'
  const header = document.createElement('header')
  header.className = 'admin-modal-header'
  const titleWrap = document.createElement('div')
  const h2 = document.createElement('h2')
  const p = document.createElement('p')
  h2.textContent = '다음 정산 회차로 이월'
  p.textContent = `선택한 ${keys.length}건을 현재 회차에서 제외하고 다음 회차의 검토 대상으로 넘깁니다.`
  titleWrap.append(h2, p)
  const close = document.createElement('button')
  close.type = 'button'
  close.className = 'admin-modal-close'
  close.setAttribute('aria-label', '닫기')
  close.textContent = '×'
  header.append(titleWrap, close)

  const body = document.createElement('div')
  body.className = 'admin-modal-body'
  const field = document.createElement('label')
  field.className = 'admin-form-field is-wide'
  const label = document.createElement('span')
  label.textContent = '이월 사유 *'
  const textarea = document.createElement('textarea')
  textarea.required = true
  textarea.placeholder = '예: 정산계좌 확인 필요, PG 수수료 대사 미완료'
  field.append(label, textarea)
  body.appendChild(field)

  const footer = document.createElement('footer')
  footer.className = 'admin-modal-footer'
  const cancel = document.createElement('button')
  cancel.type = 'button'
  cancel.className = 'button soft'
  cancel.textContent = '취소'
  const submit = document.createElement('button')
  submit.type = 'submit'
  submit.className = 'button primary'
  submit.textContent = '다음 회차로 이월'
  footer.append(cancel, submit)
  form.append(header, body, footer)
  layer.appendChild(form)
  document.body.appendChild(layer)

  const cleanup = () => { layer.remove(); document.body.style.overflow = '' }
  document.body.style.overflow = 'hidden'
  close.addEventListener('click', cleanup)
  cancel.addEventListener('click', cleanup)
  layer.addEventListener('mousedown', (event) => { if (event.target === layer) cleanup() })
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    const reason = text(textarea.value)
    if (!reason) { textarea.focus(); return }
    deferSettlement(route, keys, reason)
    cleanup()
  })
  textarea.focus()
}

function deferSettlement(route, keys, reason) {
  const batchNo = text(pageTitle()?.textContent)
  const targetBatch = nextBatchName(batchNo)
  const state = settlementState(route)
  const ledger = read(CARRYOVER_KEY, [])
  const rowMap = new Map(settlementRows().map((row) => [row.dataset.settlementKey, row]))
  let count = 0

  keys.forEach((key) => {
    if ((state[key]?.status || 'PENDING') !== 'PENDING') return
    const row = rowMap.get(key)
    if (!row) return
    const snapshot = rowData(row)
    const carryoverId = `carry-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
    const at = new Date().toISOString()
    state[key] = { status: 'DEFERRED', reason, targetBatch, at, carryoverId }
    ledger.unshift({ id: carryoverId, sourceBatch: batchNo, targetBatch, reason, at, ...snapshot })
    count += 1
  })

  saveSettlementState(route, state)
  write(CARRYOVER_KEY, ledger.slice(0, 500))
  showNotice(`${count}건을 ${targetBatch}로 이월했습니다. 이월 금액은 현재 회차 지급액에서 제외되고 다음 회차 검토 대상으로 이어집니다.`)
  refreshSettlement(route)
}

function confirmSettlementBatch(route) {
  if (!currentAdminCan(route, 'approve')) return showNotice('이 계정에는 승인·처리 권한이 없습니다.')
  const totals = settlementTotals(route)
  if (totals.PENDING) return showNotice(`검토중 ${totals.PENDING}건이 남아 있어 회차를 확정할 수 없습니다.`)
  if (!window.confirm(`이번 회차 승인 지급액 ${won(totals.approvedAmount)}을 확정할까요?\n다음 회차 이월액: ${won(totals.deferredAmount)}`)) return

  const id = route.split('/').pop()
  const batches = read(BATCH_KEY, [])
  write(BATCH_KEY, batches.map((batch) => batch.id === id ? {
    ...batch,
    status: 'PAYABLE',
    payoutAmount: totals.approvedAmount,
    deferredAmount: totals.deferredAmount,
    confirmedAt: new Date().toISOString(),
  } : batch))
  window.location.reload()
}

function applySettlementWorkflow(route) {
  if (/^\/settlements\/[^/]+$/.test(route)) refreshSettlement(route)
}

export function useAdminWorkflowPolicy(route) {
  useEffect(() => {
    const apply = () => {
      applyDirectorApprovalPolicy(route)
      applyProjectParity(route)
      applySettlementWorkflow(route)
    }
    apply()

    const afterClick = () => window.setTimeout(apply, 0)
    const onChange = (event) => {
      if (!/^\/settlements\/[^/]+$/.test(route) || !event.target.matches?.('[data-select-all]')) return
      settlementRows().forEach((row) => {
        const checkbox = row.querySelector('[data-settlement-select]')
        if (checkbox && !checkbox.disabled) checkbox.checked = event.target.checked
      })
    }
    const onClick = (event) => {
      if (!/^\/settlements\/[^/]+$/.test(route)) return
      const button = event.target.closest('button')
      if (!button) return

      if (button.dataset.rowAction === 'approve') {
        event.preventDefault()
        return approveSettlement(route, [button.dataset.key])
      }
      if (button.dataset.rowAction === 'defer') {
        event.preventDefault()
        return openCarryoverModal(route, [button.dataset.key])
      }
      if (button.dataset.bulk === 'approve-selected') {
        event.preventDefault()
        return approveSettlement(route, selectedSettlementKeys())
      }
      if (button.dataset.bulk === 'defer-selected') {
        event.preventDefault()
        return openCarryoverModal(route, selectedSettlementKeys())
      }
      if (button.dataset.bulk === 'approve-all') {
        event.preventDefault()
        const state = settlementState(route)
        const keys = settlementRows().map((row) => row.dataset.settlementKey).filter((key) => (state[key]?.status || 'PENDING') === 'PENDING')
        return approveSettlement(route, keys)
      }
      if (text(button.textContent) === '회차 확정') {
        event.preventDefault()
        event.stopImmediatePropagation()
        return confirmSettlementBatch(route)
      }
    }

    document.addEventListener('click', afterClick)
    document.addEventListener('click', onClick, true)
    document.addEventListener('change', onChange, true)
    return () => {
      document.removeEventListener('click', afterClick)
      document.removeEventListener('click', onClick, true)
      document.removeEventListener('change', onChange, true)
      document.querySelector('[data-carryover-modal]')?.remove()
    }
  }, [route])
}
